/**
 * T8 osnap-query bridge, own-review finding + local Codex round-2 P2 — shared
 * OFFSET-SPACE → TRUE-WORLD corrector for a `SceneGeometry` / `RenderableEntity`
 * produced by a renderer-facing builder.
 *
 * # Why this exists
 *
 * Every `scene3d/adapters/typed/*` per-kind adapter (`lineFromEntity` /
 * `circleFromEntity` / `arcFromEntity` / … — reached via `blockFromEntity` for a
 * block-INSERT's children) and `scene3d/adapters/geom/revisionCloudGeometry.ts`'s
 * `buildRevisionCloudSceneGeometry` call `applyOffset` (`../coordinateOrigin.ts`)
 * on every point they emit, so the Three.js renderer never sees a
 * Float32-precision-losing coordinate under a locked session origin (a DXF with
 * UTM/state-plane survey-scale coordinates, 1e6..1e9 range). That offset is a
 * RENDERER-ONLY concern.
 *
 * Two consumers OUTSIDE the renderer read this same `RenderableEntity`/
 * `SceneGeometry` output directly:
 *
 *   - `state/captureSnapshot.ts`'s `blockFlattenedChildren` facet (reuses
 *     `blockFromEntity` to flatten a block INSERT's children for `osnap-query`).
 *   - `ai/sceneQuery/osnapQuery.ts`'s `"Revision Cloud"` arm of `toSceneGeometry`
 *     (reuses `buildRevisionCloudSceneGeometry` for the scalloped cloud curve).
 *
 * Both routes report `point: {x,y}` to an AI agent alongside the query point and
 * every OTHER entity's geometry, all of which are TRUE document-world coordinates
 * (`state/captureEntityGeometry.ts`'s `projectGeometry` reads `entity.start` /
 * `.center` / etc. VERBATIM off the typed model, never calling `applyOffset`).
 * Leaking the renderer's offset-space shift into just these two facets would
 * silently hand the agent a WRONG coordinate for any document with a locked
 * non-trivial origin — this module is the one-truth inverse-offset choke point
 * both call sites reuse instead of duplicating the correction (or, worse, one of
 * them forgetting it).
 *
 * # Scope — the 6 kinds `osnap-query` actually reads
 *
 * `correctSceneGeometryToWorld` corrects POINT fields only for the 6
 * `SceneGeometry.kind`s `ai/sceneQuery/osnapQuery.ts`'s
 * `OSNAP_QUERY_CHILD_GEOMETRY_KINDS` set consumes from a flattened block child —
 * `line` / `circle` / `arc` / `ellipse` / `spline` / `polyline` — the same set
 * `osnap-query`'s own kind-filter already gates on. Every OTHER
 * `SceneGeometry.kind` (`text` / `mtext` / `hatch` / `dimension` / `multileader` /
 * `wipeout` / `viewport` / `point` / `block-insert` / `mesh-wire` / `array-rect`) is returned
 * UNCHANGED (still offset-space) — deliberately, not an oversight: NEITHER call
 * site ever reads a flattened child's geometry for one of those kinds (`osnap-query`
 * filters them out before use; a `"Revision Cloud"` never produces one of those
 * kinds in the first place), so correcting them would be dead, untestable code
 * for a shape this module's two callers never see. If a future consumer needs one
 * of these kinds corrected too, extend the `switch` below rather than assume this
 * module already covers it.
 *
 * # VECTOR fields are NEVER corrected
 *
 * A vector (a coordinate DIFFERENCE, not a point) is translation-invariant —
 * subtracting/adding a constant origin to BOTH ends of a difference cancels out,
 * so `unapplyOffset`-ing a vector would silently double-transform it. NEVER
 * corrected here: `ellipse.majorAxis` / `ellipse.extrusion` (direction vectors
 * relative to `center` — `types.ts`'s own doc comments), `spline.tangentVectors`
 * (raw DELTAS, not points — `splineFromEntity.ts`'s doc comment), `polyline.bulges`
 * / `.widths` (scalars, not vectors at all).
 */

import { unapplyOffset } from "../coordinateOrigin";
import type { RenderableEntity, SceneGeometry } from "../types";

/**
 * Correct every POINT field of a `SceneGeometry` back to TRUE document-world
 * coordinates via `unapplyOffset` (the documented inverse of the `applyOffset`
 * every typed adapter / `buildRevisionCloudSceneGeometry` applies internally).
 * Identity (returns the SAME shape, unchanged values) when no origin is locked
 * (`unapplyOffset`'s own `null`-origin no-op — the universal test-default).
 *
 * See the module header for the exact per-kind field list + the point-vs-vector
 * distinction. Kinds outside the 6 handled below are returned UNCHANGED (pass
 * through, still offset-space) — see the module header "Scope" section for why
 * that is a deliberate, documented no-op rather than a fidelity gap.
 */
export function correctSceneGeometryToWorld(geometry: SceneGeometry): SceneGeometry {
  switch (geometry.kind) {
    case "line":
      return { ...geometry, a: unapplyOffset(geometry.a), b: unapplyOffset(geometry.b) };
    case "circle":
      return { ...geometry, center: unapplyOffset(geometry.center) };
    case "arc":
      return { ...geometry, center: unapplyOffset(geometry.center) };
    case "ellipse":
      // `majorAxis` (direction vector) and `extrusion` (OCS normal) are NEVER
      // corrected — see the module header's VECTOR-fields note.
      return { ...geometry, center: unapplyOffset(geometry.center) };
    case "spline":
      // `tangentVectors` (when present) are DELTAS, not points — left untouched
      // by the spread; only `controlPoints` is a real point list.
      return { ...geometry, controlPoints: geometry.controlPoints.map(unapplyOffset) };
    case "polyline":
      // `bulges` / `widths` are scalar arrays, untouched by the spread.
      return { ...geometry, points: geometry.points.map(unapplyOffset) };
    default:
      return geometry;
  }
}

/**
 * Correct one flattened block-child `RenderableEntity` (the `blockFromEntity`
 * output `state/captureSnapshot.ts`'s `blockFlattenedChildren` getter caches)
 * back to TRUE document-world coordinates — both its `.geometry` (via
 * {@link correctSceneGeometryToWorld}) and its `.bounds` AABB.
 *
 * `.bounds` correction is a PURE TRANSLATION, not a re-derived AABB: `applyOffset`
 * / `unapplyOffset` are both a constant per-axis SHIFT (`v ± origin`, the SAME
 * origin for every point in the document), and an axis-aligned bounding box is
 * translation-covariant under any constant shift — `AABB(points + origin) ===
 * AABB(points) + origin`, exactly, regardless of which specific point set (tight
 * per-kind bbox, conservative full-circle bound, whatever) produced the ORIGINAL
 * `.bounds`. Translating the two corner points is therefore EXACTLY equivalent to
 * re-deriving the AABB from the corrected point set, without re-implementing (or
 * risking drifting from) whatever kind-specific bbox logic the real renderer
 * adapter used to compute the child's original `.bounds` — and it strictly
 * PRESERVES that original tightness (a circle/arc's real renderer bbox may be
 * tighter than `ai/sceneQuery/osnapQuery.ts`'s own defensive full-circle
 * approximation; re-deriving from a fresh point set here would silently discard
 * that tightness for no benefit — the RBush prefilter this bbox feeds only needs
 * "never too small", never "exactly tight").
 */
export function correctRenderableEntityToWorld(entity: RenderableEntity): RenderableEntity {
  const min = unapplyOffset({ x: entity.bounds.minX, y: entity.bounds.minY, z: 0 });
  const max = unapplyOffset({ x: entity.bounds.maxX, y: entity.bounds.maxY, z: 0 });
  return {
    ...entity,
    geometry: correctSceneGeometryToWorld(entity.geometry),
    bounds: { minX: min.x, minY: min.y, maxX: max.x, maxY: max.y },
  };
}
